import React, {useState} from "react";
import {Checkbox} from "primereact/checkbox";
import {Tag} from "primereact/tag";
import {Tooltip} from "primereact/tooltip";
import Icon from "../../system/components/icon";

const COLUMNS = [
    {key: "in_app", label: "زوفینو"},
    {key: "browser_push", label: "مرورگر"},
    {key: "email", label: "ایمیل", unavailable: true},
    {key: "mobile_push", label: "موبایل", unavailable: true},
];

const PRIORITY = {
    critical: {label: "حیاتی", severity: "danger"},
    important: {label: "مهم", severity: "warning"},
    normal: {label: "عادی", severity: "secondary"},
    low: {label: "کم‌اهمیت", severity: "secondary"},
};

// what the engine would do for an event in the Important / All presets (shown read-only outside Custom)
export function effectiveChannel(preference, event, channel) {
    if (event.mandatory) return true;
    if (preference.mode === "custom") {
        const value = preference.eventChannels?.[event.key]?.[channel];
        return value === undefined ? Boolean(event.defaults?.[channel]) : Boolean(value);
    }
    if (channel === "in_app") return true;
    if (preference.mode === "all") return channel === "email" ? Boolean(event.defaults?.email) : true;
    return Boolean(event.defaults?.[channel]);
}

export default function AdvancedSection({groups, events, preference, managerial, onPreferenceChange}) {
    const [open, setOpen] = useState(() => new Set(preference.mode === "custom" ? ["my_tasks"] : []));
    const custom = preference.mode === "custom";
    const visibleGroups = (groups || []).filter((group) => group.key !== "managerial" || managerial);

    const toggleGroup = (key) => setOpen((current) => {
        const next = new Set(current);
        if (next.has(key)) next.delete(key); else next.add(key);
        return next;
    });

    const setChannel = (event, channel, value) => {
        // switching a cell starts from the event's effective values so other channels keep their state
        const base = Object.fromEntries(COLUMNS.map((column) => [column.key, effectiveChannel(preference, event, column.key)]));
        onPreferenceChange({
            eventChannels: {
                ...(preference.eventChannels || {}),
                [event.key]: {...base, ...(preference.eventChannels?.[event.key] || {}), [channel]: value},
            },
        });
    };

    return (
        <section className="ns-card p-0 overflow-hidden">
            <div className="p-3 md:p-4">
                <h2 className="ns-card-title"><span className="ns-icon"><i className="pi pi-list-check"/></span>اعلان‌های سفارشی</h2>
                <p className="ns-card-description mb-0">
                    {custom
                        ? "برای هر رویداد مشخص کنید از کدام کانال به شما اطلاع داده شود."
                        : "این جدول نتیجه سطح انتخاب‌شده را نشان می‌دهد. برای تغییر هر خانه، سطح را روی «سفارشی» بگذارید."}
                </p>
                <div className="flex flex-wrap gap-2 mt-2 text-xs text-500 align-items-center">
                    <span>راهنمای اولویت:</span>
                    {["critical", "important", "normal"].map((key) => <Tag key={key} value={PRIORITY[key].label} severity={PRIORITY[key].severity} rounded/>)}
                    <span>اعلان حیاتی همیشه دریافت می‌شود؛ موارد کم‌اهمیت برای جلوگیری از شلوغی محدودند.</span>
                </div>
            </div>
            <Tooltip target=".locked-event" position="top"/>
            {visibleGroups.map((group) => {
                const groupEvents = (events || []).filter((event) => event.group === group.key);
                if (!groupEvents.length) return null;
                const expanded = open.has(group.key);
                return (
                    <div key={group.key} className="border-top-1 border-100">
                        <button
                            type="button"
                            className="ns-plain-button w-full flex align-items-center justify-content-between gap-3 p-3 md:px-4"
                            onClick={() => toggleGroup(group.key)}
                            aria-expanded={expanded}
                        >
                            <span className="flex align-items-center gap-3">
                                <span className="w-2rem h-2rem border-circle surface-100 flex align-items-center justify-content-center text-primary">
                                    <Icon icon={`mi mi:${group.icon}`}/>
                                </span>
                                <span className="flex flex-column gap-1">
                                    <strong className="text-sm text-900">{group.label}</strong>
                                    <small className="text-xs text-500">{group.description}</small>
                                </span>
                            </span>
                            <i className={`pi pi-chevron-down text-500 transition-transform transition-duration-200 ${expanded ? "rotate-180" : ""}`}/>
                        </button>
                        {expanded && (
                            <div className="px-3 md:px-4 pb-3 overflow-x-auto">
                                {group.key === "security" && (
                                    <div className="text-xs text-red-700 bg-red-50 border-round p-2 mb-2">هشدارهای امنیتی حیاتی قابل خاموش‌کردن کامل نیستند.</div>
                                )}
                                <table className="ns-matrix w-full text-xs" style={{borderCollapse: "collapse", minWidth: "34rem"}}>
                                    <thead>
                                        <tr className="text-500">
                                            <th className="text-right font-medium p-2">رویداد</th>
                                            {COLUMNS.map((column) => <th key={column.key} className="font-medium p-2 text-center" style={{width: "4.5rem"}}>{column.label}</th>)}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {groupEvents.map((event) => (
                                            <tr key={event.key} className="border-top-1 border-100">
                                                <td className="p-2">
                                                    <span className="flex align-items-center gap-2 flex-wrap">
                                                        <span className="text-800">{event.label}</span>
                                                        {["critical", "important"].includes(event.priority) && (
                                                            <Tag value={PRIORITY[event.priority].label} severity={PRIORITY[event.priority].severity} rounded className="text-2xs"/>
                                                        )}
                                                    </span>
                                                </td>
                                                {COLUMNS.map((column) => {
                                                    const locked = event.mandatory && !column.unavailable;
                                                    const disabled = !custom || column.unavailable || locked;
                                                    return (
                                                        <td key={column.key} className="p-2 text-center">
                                                            <span
                                                                className={locked ? "locked-event" : ""}
                                                                data-pr-tooltip={locked ? "این اعلان برای امنیت حساب قابل غیرفعال‌سازی نیست." : undefined}
                                                            >
                                                                <Checkbox
                                                                    checked={!column.unavailable && effectiveChannel(preference, event, column.key)}
                                                                    disabled={disabled}
                                                                    onChange={(change) => setChannel(event, column.key, change.checked)}
                                                                    aria-label={`${event.label} — ${column.label}`}
                                                                />
                                                            </span>
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                );
            })}
            <div className="border-top-1 border-100 p-3 md:px-4 text-xs text-500">
                تنظیمات اختصاصی هر پروژه، تیم یا هدف در مرحله بعد از داخل همان بخش‌ها اضافه می‌شود.
            </div>
        </section>
    );
}
